import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Button, Divider, Form, Radio, Space, Typography } from 'antd';
import { useLazyQuery, useQuery } from '@apollo/client/react';

import AddressDisplay from './AddressDisplay';
import StepContainer from './StepContainer';
import RemoveStatusModal from './AccountStatusSection/RemoveStatusModal';

import EditCustomerAddressForm from 'components/EditCustomerAddressForm/EditCustomerAddressForm';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import useModalControls from 'hooks/useModalControls';
import { ACCOUNT_STATUSES } from 'queries/account';
import { CUSTOMER_META } from 'queries/customer';

const { Title, Paragraph } = Typography;

const AddressReviewStep = ({
  containerStyles,
  checkReturnMailStatus = false,
  addressIsCurrent,
  addressUpdated,
  initialAddressInfo,
  setAddressIsCurrent,
  setAddressUpdated,
  setInitialAddressInfo,
  onContinue = () => {},
  onExit = () => {},
}) => {
  const { accountId, customerId } = useParams();
  const [addressReviewForm] = Form.useForm();
  const removeStatusModal = useModalControls();
  const [returnMailStatus, setReturnMailStatus] = useState(undefined);

  const customerMetaQuery = useQuery(CUSTOMER_META, {
    variables: { customerId },
    notifyOnNetworkStatusChange: true,
  });

  const [getAccountStatuses, accountStatusQuery] = useLazyQuery(
    ACCOUNT_STATUSES,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );

  const handleContinue = async () => {
    try {
      addressReviewForm.validateFields();
    } catch (error) {
      return;
    }

    if (checkReturnMailStatus && addressIsCurrent) {
      try {
        const response = await getAccountStatuses({
          variables: {
            customerId,
            accountIds: [accountId],
          },
        });
        const accountStatusData = (
          response.data?.accountFraudStatuses || []
        ).find((statusObj) => statusObj.accountId === accountId);

        const accountStatuses = accountStatusData?.accountStatuses || [];
        const returnMail = accountStatuses.find((status) => {
          return status.statusCode === 'RM';
        });
        setReturnMailStatus(returnMail);

        if (returnMail) {
          return;
        }
      } catch {}
    }

    if (!addressIsCurrent && !addressUpdated) {
      return;
    }
    onContinue();
  };

  const handleSelection = (event) => {
    setAddressIsCurrent(event.target.value);
  };

  const renderAddressUpdate = () => {
    return (
      <>
        <Divider />
        <Title level={5}>Update Customers Address</Title>
        <EditCustomerAddressForm
          onSuccess={() => setAddressUpdated(true)}
          submitProps={{ type: 'default' }}
        />
        <Divider style={{ marginBottom: '0px' }} />
      </>
    );
  };

  const renderReturnMailStatusCheck = () => {
    return returnMailStatus ? (
      <div style={{ marginBottom: 'var(--spacing)' }}>
        <Divider />
        <Title level={5}>Return Mail Status</Title>
        <Paragraph>Remove "Return Mail" status before proceeding</Paragraph>
        <Button
          data-testid="remove-rm-status-button"
          onClick={removeStatusModal.show}
        >
          Remove Return Mail Status
        </Button>
        {returnMailStatus ? (
          <Typography.Paragraph
            type="danger"
            style={{ marginTop: 'var(--spacing-xs)' }}
          >
            You must remove Return Mail status before proceeding
          </Typography.Paragraph>
        ) : undefined}
      </div>
    ) : null;
  };

  const renderSelectionResult = () => {
    if (addressIsCurrent === undefined) {
      return;
    }

    return addressIsCurrent
      ? renderReturnMailStatusCheck()
      : renderAddressUpdate();
  };

  useEffect(() => {
    // Had to move this out of the onComplete of customerMetaQuery because
    // it was regularly throwing an error due to render conflicts with parent component SendCardSection.
    // This is wonky but makes sure there are no stateupdate/render conflicts.
    if (customerMetaQuery.data && !initialAddressInfo) {
      setInitialAddressInfo(
        customerMetaQuery.data.customer.contactInfo.address,
      );
    }
  }, [customerMetaQuery.data]);

  return (
    <>
      <RemoveStatusModal
        {...removeStatusModal}
        statusToRemove={returnMailStatus}
      ></RemoveStatusModal>
      <StepContainer
        title="Verify Card Holder Address"
        onContinue={handleContinue}
        onExit={onExit}
        buttonProps={{
          disabled: accountStatusQuery.loading,
          loading: accountStatusQuery.loading,
        }}
        style={containerStyles}
      >
        <QueryBoundary
          query={customerMetaQuery}
          mode={QueryBoundary.MODE.MESSAGE}
          loadingMessage="Loading Card Holder Address"
        >
          <div style={{ marginBottom: 'var(--spacing)' }}>
            <AddressDisplay address={initialAddressInfo} />
          </div>
          <Form
            form={addressReviewForm}
            name="addressReview"
            layout="vertical"
            initialValues={{ addressIsCurrent }}
            disabled={addressUpdated}
          >
            <Form.Item
              name="addressIsCurrent"
              rules={[
                { required: true, message: 'Address status is required' },
              ]}
              label="Confirm with the customer that the above address is
              accurate"
            >
              <Radio.Group onChange={handleSelection} disabled={addressUpdated}>
                <Space orientation="vertical">
                  <Radio data-testid="address-is-accurate-radio" value={true}>
                    Yes, address is accurate
                  </Radio>
                  <Radio data-testid="address-needs-update-radio" value={false}>
                    No, address requires update
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </Form>
          {renderSelectionResult()}
        </QueryBoundary>
      </StepContainer>
    </>
  );
};

export default AddressReviewStep;
